import { Ref, useRef } from 'react';

import {
  faCircleNotch,
  faListCheck,
  faMagnifyingGlass,
  faPlusCircle,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import DocViewerFact from '../DocViewerFact';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import AskKim from '@/assets/icons/question-file.svg?react';
import Button from '@/components/atoms/Button';
import { Fact } from '@/types';

interface DocViewerFactSidebarProps {
  docId: string;
  factTotal: number | null;
  isLoading: boolean;
  jumpToHighlightArea: (id: string) => void;
  highlightedFactId: string | null;
  selectFact: (fact: Fact) => void;
  facts: Fact[];
  listRef: Ref<List>;
  setFactAdderModalIsOpen: (bool: boolean) => void;
}

const DocViewerFactSidebar = ({
  docId,
  factTotal,
  jumpToHighlightArea,
  highlightedFactId,
  selectFact,
  facts,
  listRef,
  isLoading,
  setFactAdderModalIsOpen,
}: DocViewerFactSidebarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleAskKim = () => {
    trackCustomEvent(TrackingEvents.AskKimFromDocViewer, { docId });
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('selectedDocId', docId);
    navigate('/app/chronos/case-editor/assistant?' + searchParams.toString());
  };

  const Row = ({ index, style }: ListChildComponentProps) => {
    const fact = facts[index];
    if (!fact) return null;

    return (
      <div style={style} className="flex items-center gap-2 py-2 pl-0.5">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="h-3 w-3 flex-shrink-0 cursor-pointer rounded-full border px-2 py-1.5 shadow hover:bg-gray-100"
          onClick={() => jumpToHighlightArea(fact.id)}
        />
        <div
          id={`fact-${fact.id}`}
          className={`flex-grow cursor-pointer rounded-md border bg-white shadow-sm transition-all duration-300 ease-in-out hover:bg-gray-100 ${
            highlightedFactId === fact.id ? 'border-brandSecondary' : ''
          }`}
          onClick={() => {
            trackCustomEvent(TrackingEvents.JumpToFactInDocFromDocFactsList, { docId });
            selectFact(fact);
            jumpToHighlightArea(fact.id);
          }}
        >
          {<DocViewerFact fact={fact} />}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-5/12 flex-col border-l border-gray-300 bg-gray-50">
      <div className="flex h-14 w-full items-center justify-between border-b bg-white pl-4 font-medium shadow-sm">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faListCheck} />
          Facts <span className="text-sm text-gray-500">{factTotal ? `(${factTotal})` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            icon={<AskKim className="h-4 w-4" />}
            text="Ask Kim"
            onClick={handleAskKim}
            type="primary"
            size="small"
            className="bg-buttonSecondary hover:bg-buttonSecondary-hover flex items-center gap-2 rounded border px-2 py-2 text-xs text-gray-700 shadow"
          />
          <Button
            icon={<FontAwesomeIcon icon={faPlusCircle} className="pr-2" />}
            text="Add Fact"
            onClick={() => setFactAdderModalIsOpen(true)}
            type="primary"
            size="small"
            className="bg-buttonSecondary hover:bg-buttonSecondary-hover rounded border px-2 py-2 text-xs text-gray-700 shadow"
          />
        </div>
      </div>
      <div id="document-facts-container" className="flex-1 overflow-y-auto" ref={containerRef}>
        <div className="flex h-full flex-col gap-2 bg-gray-50 py-1 pt-2 pr-1 pb-3 pl-2">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <FontAwesomeIcon icon={faCircleNotch} className="fa-spin h-4 w-4" />
            </div>
          ) : facts.length > 0 ? (
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => (
                <List ref={listRef} height={height} itemCount={facts.length} itemSize={72} width={width}>
                  {Row}
                </List>
              )}
            </AutoSizer>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <FontAwesomeIcon icon={faQuestionCircle} className="h-6 w-6 text-gray-500" />
              <span className="text-sm text-gray-500">Could not find any facts in this document.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocViewerFactSidebar;
